import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, EditorState } from 'lexical';
import { FC, useCallback, useEffect, useRef } from 'react';
import type { HtmlConverter } from '../converters';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';

type HtmlValuePluginProps = {
  converter: HtmlConverter;
  value?: string;
  onChange?: (newValue: string) => void;
};

export const HtmlValuePlugin: FC<HtmlValuePluginProps> = ({ converter, value, onChange }) => {
  const htmlRef = useRef('');
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (value !== htmlRef.current && value) {
      editor.update(() => {
        const convertedValue = converter ? converter.convertToEditorHtml(value) : value;
        const parser = new DOMParser();
        const dom = parser.parseFromString(convertedValue, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);

        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
    }
  }, [converter, editor, value]);

  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const raw = $generateHtmlFromNodes(editor, null);
        const html = converter ? converter.convertFromEditorHtml(raw) : raw;
        if (htmlRef.current !== html) {
          htmlRef.current = html;
          onChange?.(html);
        }
      });
    },
    [converter, editor, onChange]
  );

  return <OnChangePlugin onChange={handleChange} />;
};
